import { useState, useEffect, useMemo } from 'react';
import { routesConfig, type RouteConfig } from './routes.config';

// 404 Not Found component
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center space-y-4">
        <h1 className="text-6xl font-bold text-gray-800 dark:text-gray-200">404</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">Page not found</p>
        <a
          href="/"
          className="inline-block px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

export function Router() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  // Listen to browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
      setSearchParams(new URLSearchParams(window.location.search));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Find matching route and extract params
  const currentRoute = useMemo<RouteConfig & { params?: Record<string, string> } | null>(() => {
    for (const route of routesConfig) {
      const routePath = route.path;
      const paramNames: string[] = [];
      const regexPattern = routePath.replace(/:(\w+)/g, (_, paramName) => {
        paramNames.push(paramName);
        return '([^/]+)';
      });
      const regex = new RegExp(`^${regexPattern}$`);
      const match = currentPath.match(regex);
      
      if (match) {
        const params: Record<string, string> = {};
        paramNames.forEach((name, index) => {
          const value = match[index + 1];
          if (value) {
            params[name] = value;
          }
        });
        
        // Also include query parameters
        searchParams.forEach((value, key) => {
          params[key] = value;
        });
        
        return { ...route, params };
      }
    }
    return null;
  }, [currentPath, searchParams]);

  // Update document title
  useEffect(() => {
    if (currentRoute?.title) {
      document.title = currentRoute.title;
    }
  }, [currentRoute]);

  // Render the matched component or 404
  if (!currentRoute) {
    return <NotFound />;
  }

  const Component = currentRoute.component;
  return <Component {...(currentRoute.params || {})} />;
}

// Navigation helper function - use this to navigate programmatically
export function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Link component for navigation without page reload
export function Link({ to, children, className = '' }: { to: string; children: React.ReactNode; className?: string }) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigateTo(to);
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}
